/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { css } from '@emotion/react';
import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { Ban, Check, Search } from 'lucide-react';
import { APPLICATION_FILTER_LABELS } from 'modules/teams/domain/constants/application-status-labels';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { describeApplicationSaveError } from 'modules/teams/domain/utils/application-save-error';
import { ChangeEvent, useCallback } from 'react';
import { toast } from 'react-toastify';
import { theme } from 'styles/theme/theme';
import { UDCheckbox } from 'ud-form/components';
import UDInput from 'ud-form/components/input/component';
import UDIconButton from 'ud-ui/components/icon-button';
import { useAsyncButton } from 'ud-ui/hooks/use-async-button';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import FilterButton from './filter-button';

export type ChampionshipApplicationFiltersOption<StatusType> = {
  status: StatusType;
  color: string;
};

export type ChampionshipTeamsFilterValues<StatusType> = {
  name: string;
  status: StatusType;
};

type ChampionshipApplicationFiltersProps<StatusType> = {
  selectedStatus: StatusType;
  name?: string;
  options: ChampionshipApplicationFiltersOption<StatusType>[];
  inputPlaceholder?: string;
  countByStatus?: {
    [key: string]: number;
  };
  onSelect?: (status: AnnouncedTeamStatus) => void;
  onChange?: (values: ChampionshipTeamsFilterValues<StatusType>) => void;
  className?: string;
  selectedIds?: number[];
  onAccept?: () => Promise<void> | void;
  onDecline?: () => Promise<void> | void;
  isAllSelected?: boolean;
  onToggleSelectAll?: () => void;
};

function ChampionshipApplicationFilters<StatusType>(props: ChampionshipApplicationFiltersProps<StatusType>) {
  const {
    selectedStatus,
    name = '',
    options,
    inputPlaceholder,
    countByStatus,
    className,
    onChange,
    selectedIds = [],
    onAccept,
    onDecline,
    isAllSelected,
    onToggleSelectAll,
  } = props;

  const { t } = useLingui();

  const handleSelect = useCallback(
    (status: StatusType) => () => {
      if (onChange && status !== selectedStatus) {
        onChange({
          name,
          status,
        });
      }
    },
    [onChange, selectedStatus, name],
  );

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange({
          name: event.target.value,
          status: selectedStatus,
        });
      }
    },
    [onChange, selectedStatus],
  );

  const { isLoading: isAcceptLoading, handleClick: onClickAccept } = useAsyncButton(onAccept, {
    onError: (error) => toast.error(describeApplicationSaveError(error, msg`Не удалось принять игроков`)),
  });
  const { isLoading: isDeclineLoading, handleClick: onClickDecline } = useAsyncButton(onDecline, {
    onError: (error) => toast.error(describeApplicationSaveError(error, msg`Не удалось отклонить игроков`)),
    onSuccess: () => toast.success(t`Игроки отклонены`),
  });

  const showAcceptButton = selectedStatus === ApplicationStatus.PENDING || selectedStatus === ApplicationStatus.DECLINED;
  const showDeclineButton = selectedStatus === ApplicationStatus.PENDING || selectedStatus === ApplicationStatus.ACCEPTED;

  return (
    <div className={classNames('flex items-center', className)}>
      {onToggleSelectAll && (
        <div className="select-all-checkbox flex-center" style={{ width: 60, minWidth: 60 }}>
          <UDCheckbox checked={isAllSelected} onClick={() => onToggleSelectAll()} />
        </div>
      )}
      <div className="flex" css={cssBlockButtons(selectedIds.length > 0, showAcceptButton, showDeclineButton)}>
        {showAcceptButton && (
          <UDIconButton
            aria-label={t`Заявить`}
            icon={<Check />}
            variant="success"
            tooltip={t`Заявить`}
            onClick={onClickAccept}
            disabled={isDeclineLoading}
            loading={isAcceptLoading}
          />
        )}
        {showDeclineButton && (
          <UDIconButton
            aria-label={t`Отклонить`}
            icon={<Ban />}
            variant="danger"
            tooltip={t`Отклонить`}
            onClick={onClickDecline}
            disabled={isAcceptLoading}
            loading={isDeclineLoading}
          />
        )}
      </div>
      <div style={{ width: 270 }}>
        <UDInput
          style={{ border: '1px solid #E7E7E7' }}
          value={name}
          placeholder={inputPlaceholder}
          iconProps={{
            position: 'icon-left',
            component: <Search size={16} color={theme.colors.SurfaceRomanSilver30} />,
          }}
          onChange={handleOnChange}
        />
      </div>

      <div className="ml-auto flex">
        {options.map((item) => {
          const isActive = item.status === selectedStatus;
          const statusString = item.status as any as string;
          const statusLabel = APPLICATION_FILTER_LABELS[statusString];

          return (
            <FilterButton key={statusString} className={classNames({ active: isActive })} onClick={handleSelect(item.status)}>
              <span className="text subhead ml-2 flex items-center">
                {statusLabel ? t(statusLabel) : statusString}
                {countByStatus && countByStatus[statusString] !== undefined && (
                  <span
                    className="text-surfaceWhite ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold"
                    style={{ backgroundColor: item.color, height: 20, lineHeight: '20px' }}
                  >
                    {countByStatus[statusString]}
                  </span>
                )}
              </span>
            </FilterButton>
          );
        })}
      </div>
    </div>
  );
}

export default ChampionshipApplicationFilters;

const cssBlockButtons = (isActive: boolean, showAcceptButton: boolean, showDeclineButton: boolean) => css`
  gap: 16px;
  overflow: hidden;

  transition:
    padding ${isActive ? '0s' : '0.25s'} ease-in-out,
    margin-right 0.25s ease-in-out,
    width 0.25s ease-in-out;

  padding: ${isActive ? '1px' : '0'};
  margin-right: ${isActive ? '20px' : '0'};
  width: ${isActive ? calcWidth(showAcceptButton, showDeclineButton) : '0'};
`;

// +2px компенсируют `padding: 1px` контейнера с каждой стороны: box-sizing здесь border-box
// (глобальный Tailwind preflight), поэтому padding вычитается из заданной ширины, а не добавляется
// к ней — без запаса вторая кнопка обрезается контейнерным `overflow: hidden`.
const calcWidth = (showAcceptButton: boolean, showDeclineButton: boolean) => {
  if (showAcceptButton && showDeclineButton) {
    return '90px';
  }
  if (showAcceptButton) {
    return '38px';
  }
  if (showDeclineButton) {
    return '38px';
  }
  return '0';
};
